/** @format */

import { BehaviorSubject, Subject, take } from 'rxjs';
import { BreakpointsService, SessionStorageService } from '@shared/data-access/services';
import {
	COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY,
	COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY,
	CompareProductDetails,
	CompareService
} from '@compare/data-access';
import {
	ComponentRef,
	computed,
	effect,
	inject,
	Injectable,
	Injector,
	PLATFORM_ID,
	runInInjectionContext,
	TemplateRef
} from '@angular/core';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import {
	addCompareProduct,
	removeCompareProduct,
	setCompareBarProducts,
	setCompareProductDetails,
	setCompareProductDetailsCallFailed,
	setCompareSkeletonLoader,
	setIsCompareFeatureHidden,
	setIsCompareGridOpen
} from '@plp/data-access/state/compare/compare.actions';
import {
	selectCompareBarProducts,
	selectCompareProductDetails,
	selectIsCompareGridOpen
} from '@plp/data-access/state/compare/compare.selectors';

import { BreakPointNew } from '@shared/data-access/enums';
import { Store } from '@ngrx/store';
import { isPlatformServer } from '@angular/common';
import { CompareBarProduct } from '@shared/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class ProductListCompareService {
	private readonly store = inject(Store);
	private readonly sessionStorageService = inject(SessionStorageService);
	private readonly injector = inject(Injector);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly compareService = inject(CompareService);

	private drawerRef: ComponentRef<HmfDrawerComponent> | null = null;
	private drawerContentRef: TemplateRef<unknown> | null = null;
	private drawerWrapperClassName: string | null = null;
	private modalFactoryService;
	private readonly paintedDoorTest = new Subject<void>();

	readonly paintedDoorTest$ = this.paintedDoorTest.asObservable();
	readonly readyToInit = new BehaviorSubject(false);
	readonly compareBarList$ = this.store.selectSignal(selectCompareBarProducts);
	readonly compareGridList = this.store.selectSignal(selectCompareProductDetails);
	readonly isCompareGridOpen = this.store.selectSignal(selectIsCompareGridOpen);

	readonly drawerSize = computed(() => {
		const bp = this.breakpointsService.breakpoint();
		const compareBarListLength = this.compareBarList$().length;

		if (bp <= BreakPointNew.MEDIUM) {
			// until 1024px
			return 'large';
		} else if (bp <= BreakPointNew.EXTRALARGE) {
			if (compareBarListLength <= 2) {
				return 'medium';
			} else {
				return 'large';
			}
		} else if (compareBarListLength <= 2) {
			return 'small';
		} else if (compareBarListLength === 3) {
			return 'medium';
		} else {
			return 'large';
		}
	});
	readonly drawerDirection = computed(() =>
		this.breakpointsService.breakpoint() >= BreakPointNew.LARGE ? 'right' : 'bottom'
	);

	private lastFetchParams: {
		ecodes: string[];
		isClearance: boolean;
	} | null = null;

	constructor() {
		effect(() => {
			const size = this.drawerSize();
			const direction = this.drawerDirection();
			if (this.drawerRef) {
				this.drawerRef.setInput('size', size);
				this.drawerRef.setInput('direction', direction);
			}
		});
	}

	initCompareGrid(drawerContentRef: TemplateRef<unknown>, drawerWrapperClassName?: string): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}
		this.drawerContentRef = drawerContentRef;
		this.drawerWrapperClassName = drawerWrapperClassName || null;
		this.readyToInit.subscribe((ready) => {
			if (ready && !this.modalFactoryService) {
				runInInjectionContext(this.injector, () => {
					this.modalFactoryService = inject(HmfModalFactoryService);
				});
			}
		});
	}

	addProductToCompareList(compareBarProduct: CompareBarProduct): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.store.dispatch(addCompareProduct({ compareBarProduct }));
	}

	removeProductFromCompareList(ecode: string): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.store.dispatch(removeCompareProduct({ ecode }));
	}

	getCompareListFromSessionStorage(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const compareBarProducts: CompareBarProduct[] =
			JSON.parse(
				this.sessionStorageService.get(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '')
			) || [];

		this.store.dispatch(setCompareBarProducts({ compareBarProducts }));
	}

	clearCompareList(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.sessionStorageService.remove(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '');
		this.store.dispatch(setCompareBarProducts({ compareBarProducts: [] }));
	}

	openPaintedDoorModal(): void {
		this.paintedDoorTest.next();
		this.sessionStorageService.set(
			COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY,
			'true',
			''
		);
	}

	openCompareGrid(): void {
		if (!this.drawerRef) {
			this.drawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					className: this.drawerWrapperClassName ? this.drawerWrapperClassName : '',
					closable: true,
					direction: this.drawerDirection(),
					focus: false,
					headerText: 'Product Comparison',
					isCloseButtonSticky: true,
					mobilePeek: true,
					size: this.drawerSize()
				},
				this.drawerContentRef
			);
		}

		this.drawerRef.instance.closed.pipe(take(1)).subscribe(() => {
			this.store.dispatch(setIsCompareGridOpen({ isCompareGridOpen: false }));
		});

		this.drawerRef.instance.openDrawer();
		this.store.dispatch(setIsCompareGridOpen({ isCompareGridOpen: true }));
	}

	closeCompareGrid(): void {
		if (!this.isCompareGridOpen()) {
			return;
		}
		this.drawerRef?.instance.closeDrawer();
	}

	hasTheComparePaintedDoorTestBeenOpened(): boolean {
		if (isPlatformServer(this.platformId)) {
			return true;
		}

		// if the storage key does not exist, the feature should be shown
		const shouldTheCompareFeatureBeHidden: string =
			this.sessionStorageService.get(
				COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY,
				''
			) || 'false';
		const hasThePaintedDoorTestBeenOpened = shouldTheCompareFeatureBeHidden === 'true';
		this.store.dispatch(
			setIsCompareFeatureHidden({ isCompareFeatureHidden: hasThePaintedDoorTestBeenOpened })
		);
		if (hasThePaintedDoorTestBeenOpened) {
			this.clearCompareList();
		}

		return hasThePaintedDoorTestBeenOpened;
	}

	getProductDetailsFromApi(ecodes: string[], isClearance: boolean): void {
		this.store.dispatch(setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: true }));
		// Store the current parameters in case a retry is needed after a failed API call
		this.lastFetchParams = { ecodes, isClearance };

		//set the product details to state and loading state and error state
		this.compareService.getProductDetails(ecodes, isClearance).subscribe({
			next: (response: CompareProductDetails) => {
				this.store.dispatch(setCompareProductDetails({ productDetails: response }));
				this.store.dispatch(
					setCompareProductDetailsCallFailed({ hasProductDetailsCallFailed: false })
				);
				//TODO: This probably needs to be set after processing the data for the grid.
				this.store.dispatch(
					setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: false })
				);
			},
			error: () => {
				this.store.dispatch(
					setCompareProductDetailsCallFailed({ hasProductDetailsCallFailed: true })
				);
				this.store.dispatch(
					setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: false })
				);
			}
		});
	}

	removeProductFromCompareGrid(ecode: string): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}
		const cplProducts = this.compareGridList().products;

		const updatedList = cplProducts.filter((product) => product.style.partNumber !== ecode);

		this.store.dispatch(
			setCompareProductDetails({
				productDetails: { ...this.compareGridList(), products: updatedList }
			})
		);
		this.store.dispatch(removeCompareProduct({ ecode }));
	}

	modifyImgSrcWidthAndHeight(imgURL: string): string {
		const newImageParams = '?wid=355&hei=355&qlt=85,0&fmt=webp&op_sharpen=1';
		return imgURL.split('?')[0] + newImageParams;
	}

	/**
	 * Retry the last product details fetch using the cached parameters.
	 */
	retryLastProductDetailsFetch(): void {
		if (!this.lastFetchParams) return;
		// Extract the previously cached parameters
		const { ecodes, isClearance } = this.lastFetchParams;
		this.getProductDetailsFromApi(ecodes, isClearance);
	}
}
